import {
  AlertDialog,
  AlertDialogProps,
  JFormik,
  JItem,
  Loading,
} from '@centerpiece/components';
import {
  Container,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Typography,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import {
  PictureAsPdf,
  Close,
  Download,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useProjectContext } from 'contexts/project.context';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useCreateAttachmentMutation,
  usePatchAttachmentMutation,
  useLazyGetAttachmentQuery,
} from 'store/api';
import {
  Attachment,
  Contact,
  Project,
  Quote,
  QuoteVendor,
  QuoteVendorOrder,
} from 'store/api/models';
import { useCreateQuoteMutation, useGetQuotesQuery } from 'store/api/quote.api';
import { downloadFile } from 'utils/downloadFile.util';
import { PurchaseOrderTable } from './project/PurchaseOrderTable';
import { useGetQuoteVendorsQuery } from 'store/api/quote_vendor.api';
import { pdf } from '@react-pdf/renderer';
import { QuotePDFDocument } from './PDF/QuotePDF';
import moment from 'moment';
import { OrderMap, VendorMap } from 'contexts/purchase_order.context';
import { useGetQuoteVendorOrdersQuery } from 'store/api/quote_vendor_order.api';
import * as XLSX from 'xlsx';
import { useContacts } from 'jformik/jfield/useContacts';
import { ContactComponent } from './ContactComponent';

export const PurchaseOrders = () => {
  const {
    id,
    selectedQuote,
    setSelectedQuote,
    patchProject,
    project,
    afterSave,
  } = useProjectContext();
  const [localProject, setLocalProject] = useState<Project>(project);
  useEffect(() => {
    setLocalProject(project);
  }, [project]);
  const {
    data: quotes = [],
    refetch: refetchQuotes,
    isLoading: quotesLoading,
  } = useGetQuotesQuery(
    { params: { nested: 'all', project: id, status: 'READY' } },
    { skip: !id }
  );
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    initialValues?: any;
  }>({ open: false });
  const finalDestinationContact = useContacts({
    name: 'form.final_contacts',
    label: 'Final Destination Contact(s)',
    setOpenContact,
  });
  const {
    data: quote_vendor_orders,
    isFetching: isFetchingOrders,
    refetch: refetchQuoteVendorOrders,
  } = useGetQuoteVendorOrdersQuery(
    { params: { nested: 'all', quote: selectedQuote?.id } },
    { skip: !selectedQuote?.id, refetchOnMountOrArgChange: true }
  );
  let multiOrder: boolean = false;
  const [alertDetails, setAlertDetails] = useState<AlertDialogProps>({
    open: false,
  });
  const [vendorMap, totals] = useMemo(() => {
    let vendorMap: VendorMap = {};
    let totals = { net: 0, sell: 0, sell_total: 0 };
    quote_vendor_orders?.forEach((qvo: QuoteVendorOrder) => {
      totals.net += qvo.net || 0;
      totals.sell += qvo.sell || 0;
      totals.sell_total += qvo.sell_total || 0;
      if (vendorMap[qvo.vendor_r.id]) {
        const oqv = vendorMap[qvo.vendor_r.id];
        const qv: QuoteVendor = {
          ...qvo.quote_vendor_r,
          quote: qvo.quote,
          vendor: qvo.vendor_r.id,
          vendor_r: qvo.vendor_r,
          orders: [...oqv.orders, qvo],
          net: oqv.net + (qvo.net || 0),
          net_install: oqv.net_install + (qvo.net_install || 0),
          net_freight: oqv.net_freight + (qvo.net_freight || 0),
          sell: oqv.sell + (qvo.sell || 0),
          sell_total: oqv.sell_total + (qvo.sell_total || 0),
        } as QuoteVendor;
        multiOrder = true;
        vendorMap[qvo.vendor_r.id] = qv;
      } else {
        const qv: QuoteVendor = {
          ...qvo.quote_vendor_r,
          quote: qvo.quote,
          vendor: qvo.vendor_r.id,
          vendor_r: qvo.vendor_r,
          orders: [qvo],
          net: qvo.net || 0,
          net_install: qvo.net_install || 0,
          net_freight: qvo.net_freight || 0,
          sell: qvo.sell || 0,
          sell_total: qvo.sell_total || 0,
        } as QuoteVendor;
        multiOrder = true;
        vendorMap[qvo.vendor_r.id] = qv;
      }
    });
    return [vendorMap, totals];
  }, [quote_vendor_orders]);

  const [createQuote] = useCreateQuoteMutation();
  const [createAttachment] = useCreateAttachmentMutation();
  const [patchAttachment] = usePatchAttachmentMutation();
  const [getAttachment] = useLazyGetAttachmentQuery({ refetchOnFocus: false });
  const [uploading, setUploading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  const onSelectFile = async (event: any) => {
    const file = event.target.files.item(0);
    if (file) {
      await uploadFile(file);
    }
    event.target.value = '';
  };

  const uploadFile = async (file: any) => {
    try {
      setUploading(true);
      const attachment = await createAttachment({
        name: file.name,
        size: file.size,
        type: file.type,
        project: id,
      }).unwrap();
      toast.info('File uploading...');
      await fetch(attachment.url, {
        method: 'PUT',
        body: file,
      });
      await patchAttachment({
        id: attachment.id,
        status: 'UPLOADED',
      } as Attachment);
      toast.success('File Uploaded successfully!');
      toast.success('Processing Quote......');
      const quote = await createQuote({
        project: id,
        attachment: attachment.id,
      } as Quote).unwrap();
      toast.success('Quote processed successfully!');
      setSelectedQuote({ ...quote, name: quote?.attachment_r?.name });
      setUploading(false);
      return attachment;
    } catch (error) {
      setUploading(false);
      setAlertDetails({
        open: true,
        title: `Unable to process quote`,
        content: `Please open the file and ensure proper formatting then RESAVE it and try again`,
      });
    }
  };
  useEffect(() => {
    if (!selectedQuote && quotes.length > 0) {
      setSelectedQuote({
        ...quotes[0],
        name: quotes[0]?.attachment_r?.name,
      } as Quote);
    }
  }, [selectedQuote, quotes]);
  const [state, setState] = useState<{
    url: string | null;
    pdf: boolean;
    loading: boolean;
  }>({ pdf: false, url: null, loading: false });
  const generatePDF = () => {
    if (state.url) {
      URL.revokeObjectURL(state.url);
      setState(prevState => ({ ...prevState, loading: false, url: null }));
    } else {
      const document = (
        <QuotePDFDocument project={project} vendorMap={vendorMap} />
      );
      setState(prevState => ({ ...prevState, loading: true }));
      pdf(document)
        .toBlob()
        .then(blob => {
          setState(prevState => ({
            ...prevState,
            loading: false,
            url: URL.createObjectURL(blob),
          }));
        });
    }
  };
  const dbutton = (
    <Tooltip title={'Download xlsx file'}>
      <IconButton
        onClick={async (file: any) => {
          setDownloading(true);
          const attachment: Attachment = await getAttachment(
            `${selectedQuote?.attachment || 0}`
          ).unwrap();
          await downloadFile(attachment);
          setDownloading(false);
        }}
        color={'primary'}
      >
        {downloading ? (
          <CircularProgress color={'primary'} size={20} />
        ) : (
          <Download color={'primary'} />
        )}
      </IconButton>
    </Tooltip>
  );

  const button = state.url ? (
    <Tooltip title={'Close PDF'}>
      <IconButton onClick={generatePDF} color={'primary'}>
        {state.loading ? (
          <CircularProgress color={'primary'} size={20} />
        ) : (
          <Close color={'primary'} />
        )}
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title={'Create PDF'}>
      <IconButton onClick={generatePDF} color={'primary'}>
        {state.loading ? (
          <CircularProgress color={'primary'} size={20} />
        ) : (
          <PictureAsPdf color={'primary'} />
        )}
      </IconButton>
    </Tooltip>
  );

  const downloadToXlsx = (data: VendorMap, title: string) => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(
      [
        {
          billing_account_num: project.form?.billing_account_num,
          autoquote_id: project.form?.autoquote_id,
          final_ship_id: project.form?.final_ship_id,
          final_dest_name: project.form?.final_dest_name,
          // pre_ship_id: project.form.pre_ship_id,
          final_contacts: printContacts(project.form?.final_contacts || []),
          combine_invoices: project.form?.combine_invoices,
        },
      ] || []
    );
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Project Attributes');
    let orderAttributes: any = [];
    Object.keys(data).forEach((vendor: string) => {
      data[vendor]?.orders?.forEach(
        (order: QuoteVendorOrder, index: number) => {
          const order_name: string =
            data[vendor]?.vendor_r?.name +
            `${index > 0 ? `-${index + 1}` : ``}`;
          const prelim_address =
            order.form?.prelim_address?.form?.prelim_address;
          orderAttributes = [
            ...orderAttributes,
            {
              order_name,
              // ...order.form,
              quote_vendor_order: order.id,
              prelim_destination_name: order.form?.prelim_address?.name,
              prelim_address: `${
                prelim_address?.address ? `${prelim_address?.address} ` : ''
              }${prelim_address?.city ? `${prelim_address?.city} ` : ''}${
                prelim_address?.state ? `${prelim_address?.state} ` : ''
              }${prelim_address?.zipcode ? `${prelim_address?.zipcode} ` : ''}${
                prelim_address?.country ? `${prelim_address?.country} ` : ''
              }`,
              prelim_date: order.form?.prelim_address?.date,
              prelim_contact: printContacts(
                order.form?.prelim_address?.form?.prelim_contact || []
              ),
              // final_destination_name: order.form?.final_address?.name,
              final_address: `${
                order.form?.final_address?.address
                  ? `${order.form?.final_address?.address} `
                  : ''
              }${
                order.form?.final_address?.city
                  ? `${order.form?.final_address?.city} `
                  : ''
              }${
                order.form?.final_address?.state
                  ? `${order.form?.final_address?.state} `
                  : ''
              }${
                order.form?.final_address?.zipcode
                  ? `${order.form?.final_address?.zipcode} `
                  : ''
              }${
                order.form?.final_address?.country
                  ? `${order.form?.final_address?.country} `
                  : ''
              }`,
              final_date: order.form?.final_date,
              // final_contact: printContacts(order.form?.final_contact || []),
              customer_po: order.form?.other_po,
              rep: order.form?.other_rep?.contact_r
                ? `${order.form?.other_rep?.contact_r?.firstname} ${
                    order.form?.other_rep?.contact_r?.lastname
                  }${
                    order.form?.other_rep?.contact_r?.email
                      ? ` - ${order.form?.other_rep?.contact_r?.email}`
                      : ''
                  }`
                : order.form?.other_rep?.organization_r.name,

              invoice_comments: order.form?.invoice_comments?.comment,
              ltl_instructions: order.form?.ltl_instructions?.comment,
              po_comments: order.form?.po_comments?.comment,
            },
          ];
        }
      );
    });
    const order_worksheet = XLSX.utils.json_to_sheet(orderAttributes);
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(
      workbook,
      order_worksheet,
      'All Order Attributes'
    );
    // Convert JSON data to a worksheet
    Object.keys(data).forEach((vendor: string) => {
      data[vendor]?.orders?.forEach(
        (order: QuoteVendorOrder, index: number) => {
          const order_name: string =
            data[vendor]?.vendor_r?.name?.slice(0, 20) +
            `${index > 0 ? `-${index + 1}` : ``}` +
            ' items';
          const worksheet = XLSX.utils.json_to_sheet(
            order.items?.map(i => ({
              ...i,
              // ...order.form,
            })) || []
          );
          // Add the worksheet to the workbook
          XLSX.utils.book_append_sheet(workbook, worksheet, order_name);
        }
      );
    });
    // Convert workbook to a binary string
    const workbookBinary = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'binary',
    });

    // Create a Blob object from the binary string
    const blob = new Blob([s2ab(workbookBinary)], {
      type: 'application/octet-stream',
    });

    // Generate a download link
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `${title}-${moment().format('MM-DD-YYYY')}.xlsx`;

    // Trigger the download
    downloadLink.click();
  };

  // Helper function to convert string to ArrayBuffer
  function s2ab(s: any) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  }

  if (quotesLoading || uploading) {
    return (
      <Container maxWidth="sm" sx={{ pt: 5 }}>
        <Loading
          text={
            uploading ? 'Processing Quote ...' : 'Fetching project quotes ...'
          }
        />
      </Container>
    );
  } else if (quotes?.length > 0) {
    return (
      <Container maxWidth="xl" sx={{ height: '100%', overflowY: 'scroll' }}>
        {openContact.open && (
          <ContactComponent
            close={() => setOpenContact({ open: false })}
            {...openContact}
          />
        )}
        <Grid container>
          <Grid item xs={12} flexGrow={2}>
            <Grid
              container
              alignItems={'center'}
              justifyItems={'space-between'}
              alignContent={'space-between'}
              justifyContent={'space-between'}
            >
              <Grid item xs={12}>
                <JFormik
                  initialValues={{ ...localProject }}
                  autoSaveForm={true}
                  handleSubmit={async (values, formik) => {
                    await setLocalProject(prev => ({
                      ...prev,
                      form: values.form,
                    }));
                    await patchProject({ id, form: values.form });
                    await afterSave();
                  }}
                  form={[
                    {
                      name: 'title',
                      label: 'Summary Information',
                      itemType: 'typography',
                      jItemProps: {
                        variant: 'h6',
                      },
                      gridProps: { xs: 12, lg: 10 },
                    },
                    {
                      name: 'download',
                      label: 'Export Data',
                      itemType: 'button',
                      jItemProps: {
                        onClick: ({
                          setFieldValue,
                        }: {
                          setFieldValue: Function;
                        }) => {
                          downloadToXlsx(
                            vendorMap,
                            selectedQuote?.attachment_r?.name || 'export'
                          );
                        },
                        sx: { minHeight: 'unset', height: '100%' },
                        // size: 'small',
                      },
                      gridProps: { xs: 12, lg: 2 },
                    },
                    {
                      name: 'form.billing_account_num',
                      itemType: 'textfield',
                      label: 'Billing Account *',
                      gridProps: { xs: 12, lg: 4 },
                      jItemProps: {
                        size: 'small',
                        InputProps: {
                          size: 'small',
                        },
                      },
                    },
                    {
                      name: 'form.autoquote_id',
                      itemType: 'textfield',
                      label: 'AutoQuotes ID *',
                      gridProps: { xs: 12, lg: 4 },
                      jItemProps: {
                        size: 'small',
                        InputProps: {
                          size: 'small',
                        },
                      },
                    },
                    {
                      name: 'form.combine_invoices',
                      itemType: 'checkbox',
                      label: 'Combine Invoices',
                      gridProps: { xs: 12, lg: 4 },
                      jItemProps: {
                        size: 'small',
                        InputProps: {
                          size: 'small',
                        },
                      },
                    },

                    {
                      name: 'form.final_ship_id',
                      itemType: 'textfield',
                      label: 'Final Destination ID',
                      gridProps: { xs: 12, lg: 4 },
                      jItemProps: {
                        size: 'small',
                        InputProps: {
                          size: 'small',
                        },
                      },
                    },
                    // {
                    //   name: 'form.pre_ship_id',
                    //   itemType: 'textfield',
                    //   label: 'Preliminary Destination ID',
                    //   gridProps: { xs: 12, lg: 2 },
                    //   jItemProps: {
                    //     size: 'small',
                    //     InputProps: {
                    //       size: 'small',
                    //     },
                    //   },
                    // },
                    {
                      name: 'form.final_dest_name',
                      itemType: 'textfield',
                      label: 'Final Destination Name',
                      gridProps: { xs: 12, lg: 4 },
                      jItemProps: {
                        size: 'small',
                        InputProps: {
                          size: 'small',
                        },
                      },
                    },

                    {
                      ...(finalDestinationContact as JItem),
                      // disabled: true,
                      gridProps: { xs: 12, lg: 4 },
                      jItemProps: {
                        size: 'small',
                        InputProps: {
                          size: 'small',
                        },
                      },
                    },
                    // {
                    //   name: 'download',
                    //   label: 'Export Data',
                    //   itemType: 'button',
                    //   jItemProps: {
                    //     onClick: ({
                    //       setFieldValue,
                    //     }: {
                    //       setFieldValue: Function;
                    //     }) => {
                    //       downloadToXlsx(
                    //         vendorMap,
                    //         selectedQuote?.attachment_r?.name || 'export'
                    //       );
                    //     },
                    //     sx: { minHeight: 'unset', height: '100%' },
                    //     size: 'small',
                    //   },
                    //   gridProps: { xs: 12, lg: 3 },
                    // },
                    // {
                    //   name: 'send_to_erp',
                    //   label: project?.form?.send_to_erp ? (
                    //     <span>
                    //       Sent{' '}
                    //       <small>
                    //         <i>
                    //           (
                    //           {`${moment(project.form.send_to_erp).format(
                    //             'MMM D YYYY h:mm A'
                    //           )}`}
                    //           )
                    //         </i>
                    //       </small>
                    //     </span>
                    //   ) : (
                    //     'Send Order To Erp'
                    //   ),
                    //   itemType: 'button',
                    //   disabled: project?.form?.send_to_erp,
                    //   jItemProps: {
                    //     onClick: ({
                    //       setFieldValue,
                    //     }: {
                    //       setFieldValue: Function;
                    //     }) => {
                    //       setAlertDetails({
                    //         open: true,
                    //         title: `Send the order?`,
                    //         content: `Are you sure you want to send the order?`,
                    //         onConfirm: async () => {
                    //           await setFieldValue('form.send_to_erp', new Date());
                    //           await patchProject({
                    //             id,
                    //             form: { ...project.form, send_to_erp: new Date() },
                    //           });
                    //           toast.success('Order sent to ERP successfully!');
                    //         },
                    //         onClose: () => {},
                    //       });
                    //     },
                    //     sx: { minHeight: 'unset', height: '100%' },
                    //     size: 'small',
                    //   },
                    //   gridProps: { xs: 12, lg: 2 },
                    // },
                  ]}
                />
              </Grid>
              {/* <Grid xs={12} lg={2}>
                <JFormik
                  initialValues={{}}
                  form={[
                    {
                      name: 'download',
                      label: 'Export Data',
                      itemType: 'button',
                      jItemProps: {
                        onClick: ({
                          setFieldValue,
                        }: {
                          setFieldValue: Function;
                        }) => {
                          downloadToXlsx(
                            vendorMap,
                            selectedQuote?.attachment_r?.name || 'export'
                          );
                        },
                        sx: { minHeight: 'unset', height: '100%' },
                        // size: 'small',
                      },
                      gridProps: { xs: 12 },
                    },
                  ]}
                />
              </Grid> */}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Grid
              container
              direction="row"
              alignItems="stretch"
              spacing={2}
              flexWrap={'nowrap'}
              sx={{ maxWidth: '100%' }}
            >
              <Grid item xs={3} flexGrow={2}>
                <JFormik
                  initialValues={{}}
                  form={[
                    {
                      name: 'type',
                      itemType: 'autocomplete',
                      label: 'Select a quote',
                      value: selectedQuote,
                      options: quotes?.map(q => ({
                        ...q,
                        name: q?.attachment_r?.name,
                      })),
                      loading: isFetchingOrders,
                      jItemProps: {
                        disableClearable: true,
                        onChange: (type: Quote) => {
                          setSelectedQuote(type);
                        },
                        // InputProps: {
                        //   sx: { boxShadow: (theme: Theme) => theme.shadows[5] },
                        // },
                      },
                    },
                  ]}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  component="label"
                  sx={{ height: '100%' }}
                >
                  <input type="file" hidden onChange={onSelectFile} />
                  Upload New Quote
                  {uploading && (
                    <CircularProgress
                      color="inherit"
                      size={16}
                      sx={{ ml: 4 }}
                    />
                  )}
                </Button>
                {dbutton}
                {button}
              </Grid>
              <Grid item sx={{ ml: 'auto' }} alignSelf={'center'}>
                <Tooltip title="Compare these financials with what is reported in AutoQuotes to confirm quote accuracy">
                  <WarningAmberOutlined color="warning" />
                </Tooltip>
              </Grid>
              <Grid item>
                <Typography variant="body2">
                  <b>Total Revenue:&nbsp;&nbsp;&nbsp;&nbsp;</b>
                  <span style={{ float: 'right' }}>
                    {totals.sell_total.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </Typography>
                <Typography variant="body2">
                  <b>Total Gross Profits:&nbsp;&nbsp;&nbsp;&nbsp;</b>
                  <span style={{ float: 'right' }}>
                    {Number(totals.sell_total - totals.net).toLocaleString(
                      'en-US',
                      {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </span>
                </Typography>
              </Grid>
            </Grid>
            <Grid>
              <Box sx={{ pl: 1 }}>
                <small>
                  <i>
                    Quote uploaded on{' '}
                    {moment(selectedQuote?.attachment_r?.created_at).format(
                      'MMM D ‘YY - hh:mm a'
                    )}
                  </i>
                </small>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {state.url ? (
              <div style={{ width: '90%', height: '1000px' }}>
                <iframe
                  title={'PDF'}
                  src={state.url}
                  style={{ position: 'absolute' }}
                  width={'90%'}
                  height={'85%'}
                />
              </div>
            ) : (
              <PurchaseOrderTable
                vendorMap={vendorMap}
                refetch={refetchQuoteVendorOrders}
                multiOrder={multiOrder}
                setAlertDetails={setAlertDetails}
              />
            )}
          </Grid>
        </Grid>
        <AlertDialog
          {...alertDetails}
          onClose={() => {
            alertDetails?.onClose && alertDetails.onClose();
            setAlertDetails({ open: false });
          }}
        />
      </Container>
    );
  } else {
    return (
      <Container maxWidth="sm">
        <AlertDialog
          {...alertDetails}
          onClose={() => {
            alertDetails?.onClose && alertDetails.onClose();
            setAlertDetails({ open: false });
          }}
        />
        <JFormik
          initialValues={{}}
          // validationSchema={Yup.object({})}
          // handleSubmit={async (values: any, formik: FormikHelpers<any>) => {}}
          form={
            [
              {
                name: 'attachments',
                itemType: 'dropzone',
                label: 'Attach AutoQuotes Excel File',
                gridProps: { xs: 12 },
                jItemProps: {
                  caption: 'Drag and drop .xlsx file.',
                  onSelect: async (file: any) => {
                    await uploadFile(file);
                  },
                  // onDelete: async (file: any) => {
                  //   await deleteAttachment(file.id);
                  //   refetchItem();
                  //   afterSave();
                  // },
                  onDownload: async (file: any) => {
                    const attachment: Attachment = await getAttachment(
                      file.id
                    ).unwrap();
                    await downloadFile(attachment);
                  },
                },
              },
            ] as JItem[]
          }
        />
      </Container>
    );
  }
};

const printContacts = (contacts: (Contact & { name: string })[]) => {
  let text = '';
  contacts.forEach(contact => {
    text +=
      `${contact?.name ? `${contact?.name} ` : ''} ${
        contact?.email ? `${contact?.email}` : ''
      }` + ', ';
  });
  return text;
};
